<script setup lang="ts">
import {
  ParagraphHotspotGraphic,
  MediaImageFragment,
  ParagraphHotspotItem,
  ParagraphLegendItem,
  MediaImage,
  MediaDocument,
} from '~/api/graphql/generated'
const props = defineProps<{
  entity: ParagraphHotspotGraphic
}>()

const isModalOpen = ref(false)
const selectedEntity = ref()
const mobileContainer = ref<HTMLDivElement>()
const mobileImageLoaded = ref(false)

// Watch for image load to trigger recalculation
const onMobileImageLoad = () => {
  mobileImageLoaded.value = true
  // Force a small delay to ensure DOM has updated
  nextTick(() => {
    // Trigger reactivity by accessing the computed
    void mobileContainerScrollBox.value
  })
}

const mobileContainerScrollBox = computed(() => {
  const element = mobileContainer.value as HTMLDivElement
  if (!element || !mobileImageLoaded.value)
    return {
      x: 0,
      y: 0,
    }
  return {
    x: element.scrollWidth,
    y: element.scrollHeight,
  }
})

const hotspotDesktopItems = computed(() => {
  return props.entity.fieldHotspotDesktopItems?.map((hdi) => {
    const hotspotItem = hdi?.entity as ParagraphHotspotItem
    return {
      fieldX: hotspotItem.fieldX,
      fieldY: hotspotItem.fieldY,
      hotspotItem: hotspotItem,
    }
  })
})

const hotspotMobileItems = computed(() => {
  return props.entity.fieldHotspotMobileItems?.map((hdi) => {
    const hotspotItem = hdi?.entity as ParagraphHotspotItem
    return {
      fieldX: hotspotItem.fieldX,
      fieldY: hotspotItem.fieldY,
      hotspotItem: hotspotItem,
    }
  })
})

// Computed property for mobile hotspot positioning
const mobileHotspotPositions = computed(() => {
  if (!hotspotMobileItems.value || !mobileContainerScrollBox.value.x || !mobileContainerScrollBox.value.y) {
    return []
  }

  return hotspotMobileItems.value.map((hdi, index) => ({
    key: 'hotspot' + index,
    style: {
      top: mobileContainerScrollBox.value.y * ((Number(hdi.fieldY) || 0) / 100) + 'px',
      left: mobileContainerScrollBox.value.x * ((Number(hdi.fieldX) || 0) / 100) + 'px',
    },
    hotspotItem: hdi.hotspotItem,
  }))
})

// Computed property for desktop hotspot positioning
const desktopHotspotPositions = computed(() => {
  if (!hotspotDesktopItems.value) return []

  return hotspotDesktopItems.value.map((hdi, index) => ({
    key: 'hotspot' + index,
    style: {
      top: hdi.fieldY + '%',
      left: hdi.fieldX + '%',
    },
    hotspotItem: hdi.hotspotItem,
  }))
})

const hotspotFieldItems = computed(() => {
  return props.entity.fieldItems?.map((hfi) => {
    const legendItem = hfi?.entity as ParagraphLegendItem
    return {
      text: legendItem.fieldText?.processed,
      image: legendItem.fieldLegendIcon?.entity as MediaImage,
      imageExists: (legendItem.fieldLegendIcon?.entity as MediaImage).fieldMediaImage,
    }
  })
})

const image = computed(() => {
  if (!props.entity.fieldImage) return null
  return props.entity.fieldImage?.entity as MediaImageFragment
})

const mobileImage = computed(() => {
  if (!props.entity.fieldMobileImage) return null
  return props.entity.fieldMobileImage?.entity as MediaImageFragment
})

const downloadData = computed(() => {
  const data = props.entity.fieldDownloadItem?.entity as MediaDocument
  return {
    title: props.entity.fieldDownloadLabel || '',
    type: data?.fieldMediaDocument?.entity?.filemime || '',
    size: data?.fieldMediaDocument?.entity?.filesize || '',
    url: data?.fieldMediaDocument?.entity?.url || '',
  }
})

const fieldClicked = (item: ParagraphHotspotItem) => {
  selectedEntity.value = item.fieldOverlayItem?.entity
  isModalOpen.value = true
}
</script>

<template>
  <div class="hotspot-container desktop">
    <Image v-if="image" :entity="image" type="teaser-full" />
    <div
      v-for="position in desktopHotspotPositions"
      :key="position.key"
      class="hotspot"
      :style="position.style"
      @click="fieldClicked(position.hotspotItem)"
    >
      <div class="hotspot-center">
        <Icon name="mini-plus-small" class="hotspot-icon" />
      </div>
    </div>
  </div>
  <div ref="mobileContainer" class="hotspot-container mobile">
    <Image
      v-if="mobileImage"
      :entity="mobileImage"
      type="teaser-full"
      class="hotspot-image"
      @load="onMobileImageLoad"
    />
    <div
      v-for="position in mobileHotspotPositions"
      :key="position.key"
      class="hotspot"
      :style="position.style"
      @click="fieldClicked(position.hotspotItem)"
    >
      <div class="hotspot-center">
        <Icon name="mini-plus-small" class="hotspot-icon" />
      </div>
    </div>
  </div>
  <ModalEmpty v-model:open="isModalOpen">
    <HotspotItemModalContent :entity="selectedEntity" />
  </ModalEmpty>
  <div class="hotspot-footer">
    <div class="legend-container">
      <div v-for="(item, index) in hotspotFieldItems" :key="index" class="legend">
        <Image v-if="item.imageExists" :entity="item.image" type="teaser-full" />
        <div v-html="item.text" />
      </div>
    </div>
    <div v-if="downloadData.url" class="download-container">
      <Button secondary icon="download" :to="downloadData.url" target="_blank" rel="noopener noreferrer">
        {{ downloadData.title }}
      </Button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotspot-container {
  position: relative;
  overflow: hidden;
  margin-bottom: 20px;
  &.desktop {
    display: none;
    @include breakpoint(tl) {
      display: block;
      img {
        width: 100%;
      }
    }
  }
  &.mobile {
    height: 416px;
    overflow-x: auto;
    .hotspot-image {
      max-height: 100%;
      height: 100%;
      max-width: unset;
      width: auto;
    }
    display: block;
    @include breakpoint(tl) {
      display: none;
    }
  }
  .hotspot {
    cursor: pointer;
    width: 30px;
    height: 30px;
    position: absolute;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: x-large;
    color: white;
    @include breakpoint(tl) {
      width: 52px;
      height: 52px;
    }
    .hotspot-center {
      border-radius: 50%;
      width: 30px;
      height: 30px;
      text-align: center;
      background: var(--c-secondary);
      border: 2px solid white;
      display: flex;
      align-items: center;
      justify-content: center;
      @include breakpoint(tl) {
        width: 40px;
        height: 40px;
        .hotspot-icon {
          width: 14px;
          height: 14px;
        }
      }
      .hotspot-icon {
        color: var(--c-blue-100);
        width: 12px;
        height: 12px;
      }
    }

    &:hover {
      background: rgba(24, 36, 111, 0.2);
      .hotspot-center {
        background: var(--c-blue-100);
        border: 0;
        .hotspot-icon {
          color: white;
        }
      }
    }
  }
}
.hotspot-footer {
  padding-left: var(--layout-padding);
  padding-right: var(--layout-padding);
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  @include breakpoint(ds) {
    flex-wrap: nowrap;
    padding-left: 0;
    padding-right: 0;
  }
  .legend-container {
    .legend {
      display: flex;
      align-items: center;
      padding-bottom: 8px;
      div {
        padding-left: 16px;
        :deep(p) {
          margin-bottom: 0;
          font-size: 16px;
          font-weight: 450;
          line-height: 22px;
          letter-spacing: 0.25px;
          text-align: left;
          color: #1a1a1abf;
        }
      }
    }
  }
  .download-container {
    padding-top: 20px;
    @include breakpoint(tl) {
      padding-top: 0;
    }
  }
}
</style>
